import styled from "@emotion/styled";
import { WalletReadyState } from "@solana/wallet-adapter-base";
import type { Wallet } from "@solana/wallet-adapter-react";
import { black } from "@stepfinance/react";
import type { ModalProps } from "antd";
import { Menu, Modal } from "antd";
import type { FC, MouseEvent } from "react";
import React, { useCallback, useMemo, useState } from "react";

import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLanguageContext } from "../../contexts/language/language";
import { useWalletModal } from "./useWalletModal";
import { WalletMenuItem } from "./WalletMenuItem";

export interface WalletModalProps extends Omit<ModalProps, "visible"> {
  featuredWallets?: number;
}

export const WalletModal: FC<WalletModalProps> = ({ onCancel }) => {
  const { wallets, select } = useEnrichedWallet();
  const { visible, setVisible } = useWalletModal();
  const [expanded, setExpanded] = useState(false);

  const {
    currentLanguageData: {
      components: { walletModal },
    },
  } = useLanguageContext();

  const [installed, more] = useMemo(() => {
    if (!wallets) {
      return [[], []];
    }
    const installed: Wallet[] = [];
    const loadable: Wallet[] = [];
    const notDetected: Wallet[] = [];

    for (const wallet of wallets) {
      if (wallet.adapter.name !== "PublicKey") {
        wallet.readyState === WalletReadyState.Installed && installed.push(wallet);
        wallet.readyState === WalletReadyState.Loadable && loadable.push(wallet);
        wallet.readyState === WalletReadyState.NotDetected && notDetected.push(wallet);
      }
    }
    return [installed, loadable.concat(notDetected)];
  }, [wallets]);

  const handleCancel = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (onCancel) onCancel(event);
      if (!event.defaultPrevented) setVisible(false);
    },
    [onCancel, setVisible],
  );

  const handleWalletClick = useCallback(
    (event: MouseEvent<HTMLElement>, wallet: Wallet) => {
      if (wallet.readyState === WalletReadyState.Installed || wallet.readyState === WalletReadyState.Loadable) {
        select(wallet.adapter.name);
        handleCancel(event);
      } else {
        window.open(wallet.adapter.url, "_blank");
      }
    },
    [select, handleCancel],
  );

  const onOpenChange = useCallback(() => setExpanded(!expanded), [expanded]);

  return (
    <Modal
      title={walletModal.title}
      visible={visible}
      centered={true}
      onCancel={handleCancel}
      footer={null}
      width={400}
      maskStyle={{
        background: black,
      }}
    >
      <ModalMenu
        inlineIndent={0}
        mode="inline"
        onOpenChange={onOpenChange}
        openKeys={installed.length === 0 ? ["more-wallets"] : undefined}
      >
        {installed.map((wallet) => (
          <WalletMenuItem
            key={wallet.adapter.name}
            onClick={(event) => handleWalletClick(event, wallet)}
            wallet={wallet}
          />
        ))}
        {more.length ? (
          <Menu.SubMenu
            key={"more-wallets"}
            title={
              installed.length === 0
                ? walletModal.lessOptions
                : expanded
                ? walletModal.lessOptions
                : walletModal.moreOptions
            }
          >
            {more.map((wallet) => (
              <WalletMenuItem
                key={wallet.adapter.name}
                onClick={(event) => handleWalletClick(event, wallet)}
                wallet={wallet}
              />
            ))}
          </Menu.SubMenu>
        ) : null}
      </ModalMenu>
    </Modal>
  );
};

const ModalMenu = styled(Menu)`
  border: none;
`;
